import * as turf from '@turf/turf';
import { GeoJSONSource, Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import { v4 } from 'uuid';
import { EventBus } from '../../../app/contexts/event-bus';
import assert from '../../../utils/assert';
import { DrawingObjectType } from '../types';
import { FeDrawMode } from './draw-mode';

export class FeCircleMode extends FeDrawMode {
    constructor(
        private readonly map: MaplibreMap,
        private readonly source: GeoJSONSource
    ) {
        super();
    }

    private feature!: GeoJSON.Feature<GeoJSON.Polygon> | null;
    private clickCount = 0;
    private currentRadius = [0, 0];
    private centerPoint: number[] | null = null;

    override onClick(e: MapMouseEvent): void {
        if (this.clickCount === 0) {
            this.centerPoint = e.lngLat.toArray();
            this.feature = {
                type: 'Feature',
                id: v4(),
                properties: {
                    type: DrawingObjectType.circle,
                    radius: 0
                },
                geometry: {
                    type: 'Polygon',
                    coordinates: [[this.centerPoint, this.centerPoint]]
                }
            };
            this.source.updateData({
                add: [this.feature]
            });
            this.clickCount++;
        } else {
            this.finishCircle();
        }
    }
    override onMouseMove(e: MapMouseEvent) {
        if (this.clickCount < 1) {
            return;
        }
        assert(this.centerPoint);
        if (e.originalEvent.shiftKey) {
            this.currentRadius[0] = this.currentRadius[1] = turf.distance(this.centerPoint, e.lngLat.toArray(), { units: 'nauticalmiles' });
        } else {
            this.currentRadius[0] = turf.distance(this.centerPoint, [e.lngLat.lng, this.centerPoint[1]], { units: 'nauticalmiles' });
            this.currentRadius[1] = turf.distance(this.centerPoint, [this.centerPoint[0], e.lngLat.lat], { units: 'nauticalmiles' });
        }
        this.updateGeometry();
    }

    override onKeyDown(e: KeyboardEvent): void {
        if (e.key === 'Enter' || e.key === ' ') {
            if (this.clickCount !== 0) {
                e.preventDefault();
                this.finishCircle();
            }
        }
    }

    override cancel() {
        this.clickCount = 0;
        this.centerPoint = null;
        this.currentRadius = [0, 0];
        this.feature = null;
        this.source.setData(turf.featureCollection([]));
    }

    private finishCircle() {
        this.clickCount = 0;
        const feature = this.feature;
        this.feature = null;
        this.source.updateData({
            remove: [feature!.id!]
        });

        EventBus.fire('draw.create', {
            drawObject: {
                type: DrawingObjectType.circle,
                radius: this.currentRadius,
                center: this.centerPoint!
            }
        });
    }

    private updateGeometry() {
        assert(this.feature);
        assert(this.centerPoint);
        if (this.currentRadius[0] === 0 || this.currentRadius[1] === 0) {
            return;
        }
        this.feature.geometry = turf.ellipse(this.centerPoint, this.currentRadius[0], this.currentRadius[1], {
            units: 'nauticalmiles',
            steps: 100
        }).geometry;
        this.source.updateData({
            update: [
                {
                    id: this.feature!.id as string,
                    newGeometry: this.feature!.geometry
                }
            ]
        });
    }
}
